'use client';

import { useId } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Position, WISHLIST_MODAL_ID } from '@headless-workspace/config';
import { SideMenuModal, useTailwindBreakpoint, useModalActions } from '@headless-workspace/core-ui';
import { GlowCarousel, GlowProductTile, Icons, ModalCommonClasses, PropsWithLocale } from '@headless-workspace/glow-ds';
import type { Component } from '@headless-workspace/typings';
import { useClientData, useProductTilesMapper, useWishlist, WishlistModal } from '../../../client';
import { type TileProductValue, type VipBrandsValue } from '../../../server';
import { useProductTileCTABusinessRules } from './useProductTileCTABusinessRules';

type ProductDetailProductTile = {
    isAvailable: boolean;
};

type ProductCarouselProps = PropsWithLocale & {
    products: TileProductValue[];
    vipBrands: VipBrandsValue;
    wishlistBtnAriaLabel: string;
    enablePriceExceptionLegalMentions: boolean;
    navigationButtonClassName?: string;
};

export const ProductDetailCarousel: Component<ProductCarouselProps> = ({
    products,
    vipBrands,
    wishlistBtnAriaLabel,
    locale,
    enablePriceExceptionLegalMentions,
    navigationButtonClassName,
}) => {
    const isTablet = useTailwindBreakpoint('tablet');
    const t = useTranslations('Discover.carousel.accessibility');
    const productTileId = useId();

    const { addIfAbsentAndOpen } = useModalActions();

    const { getClientConfigs } = useClientData();

    const isNotifyMeEnabled = getClientConfigs('enableNotifyMe') ?? false;

    const { shouldAddToWishlist, update: updateWishlist, isProductInWishlist } = useWishlist();

    const { handleProductModalView, isAddingToBasket } = useProductTileCTABusinessRules({
        vipBrands,
        locale,
        isNotifyMeEnabled,
        addIfAbsentAndOpen,
    });

    const productTiles = useProductTilesMapper<ProductDetailProductTile>({
        products,
        locale,
        showStockStatusBtn: true,
        enablePriceExceptionLegalMentions,
        customMapper: (originalProduct) => {
            return {
                isAvailable: originalProduct.isAvailable ?? false,
            };
        },
    });

    return (
        <GlowCarousel
            ariaLabels={{
                carousel: t('carousel'),
                next: t('nextBtn'),
                prev: t('prevBtn'),
                product: t('product'),
                productList: t('productList'),
                progressBar: t('progressBar'),
            }}
            navigationButtonClassName={navigationButtonClassName}
            productTileId={productTileId}
        >
            {productTiles.map((product) => {
                const buttonOnClick = handleProductModalView(product.isAvailable, product);
                const icon = isNotifyMeEnabled && !product.isAvailable ? Icons.Notification : undefined;

                return (
                    <GlowProductTile
                        key={product.id}
                        {...product}
                        button={{
                            ...product.button,
                            onClick: buttonOnClick,
                            icon,
                            isLoading: isAddingToBasket(product.sku),
                        }}
                        dataLayer={product.dataLayer}
                        isTablet={isTablet}
                        ImageTagName={Image}
                        wishlist={{
                            onClick: () => {
                                if (shouldAddToWishlist) {
                                    updateWishlist({
                                        parentId: product.id,
                                        productId: product.sku,
                                        productName: product.name,
                                    });
                                } else {
                                    addIfAbsentAndOpen(
                                        WISHLIST_MODAL_ID,
                                        SideMenuModal({
                                            side: Position.None,
                                            map: {
                                                default: <WishlistModal productId={product.sku} locale={locale} />,
                                            },
                                            className: ModalCommonClasses,
                                        }),
                                    );
                                }
                            },
                            isAdded: isProductInWishlist(product.sku),
                            ariaLabel: wishlistBtnAriaLabel,
                        }}
                        legalMentionsLabel={product.legalMentionsLabel}
                    />
                );
            })}
        </GlowCarousel>
    );
};
